import { useCallback, useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";

const WEEKDAY = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "long", timeZone: "UTC" });

export function SchedulePanel({ book }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const load = useCallback(() => api.get(`/books/${book.id}/webtoon/schedule`).then((r) => { setD(r.data); setF(r.data.schedule); }), [book.id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const save = async () => { try { const { data } = await api.put(`/books/${book.id}/webtoon/schedule`, f); setD(data); setF(data.schedule); toast.success("Release schedule saved"); } catch (e) { toast.error(errMsg(e)); } };
  const inp = "h-8 rounded-md bg-slate-900 border border-slate-700 px-2 text-xs";
  const later = d.episodes.filter((e) => e.episode >= (f.from_episode || 1)).map((e) => e.episode);
  const preview = (ep) => {
    if (f.mode === "none") return null;
    if (f.mode === "dates") return f.dates?.[String(ep)] || null;
    if (!f.start || !later.includes(ep)) return null;
    const t = new Date(`${f.start}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + 7 * later.indexOf(ep)); return t.toISOString().slice(0, 10);
  };
  return (
    <div className="panel p-5 space-y-3" data-testid="webtoon-schedule">
      <p className="eyebrow flex items-center gap-1"><CalendarClock size={12} />Episode releases</p>
      <div className="flex flex-wrap gap-1">{[["none", "All live now"], ["dates", "A date per episode"], ["weekly", "Weekly release day"]].map(([k, l]) => (
        <button key={k} onClick={() => setF({ ...f, mode: k })} className={`text-xs px-3 py-1 rounded-full border ${f.mode === k ? "border-amber-400 text-amber-200" : "border-slate-700 text-slate-400"}`} data-testid={`schedule-mode-${k}`}>{l}</button>))}</div>
      {f.mode === "weekly" && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
          First release<input type="date" value={f.start || ""} onChange={(e) => setF({ ...f, start: e.target.value })} className={inp} data-testid="schedule-start" />
          starting with episode<input type="number" min="1" value={f.from_episode || 1} onChange={(e) => setF({ ...f, from_episode: Number(e.target.value) || 1 })} className={`${inp} w-16`} data-testid="schedule-from" />
          {f.start && <span className="text-amber-200">— one episode every {WEEKDAY(f.start)}</span>}
        </div>
      )}
      <div className="space-y-1 max-h-60 overflow-y-auto scrollbar-thin">
        {d.episodes.map((e) => { const u = preview(e.episode); const live = !u || u <= d.today; return (
          <div key={e.episode} className="flex items-center gap-3 text-xs" data-testid={`schedule-ep-${e.episode}`}>
            <span className="w-20 text-slate-300">Episode {e.episode}</span>
            {f.mode === "dates" ? <input type="date" value={f.dates?.[String(e.episode)] || ""} onChange={(ev) => setF({ ...f, dates: { ...f.dates, [String(e.episode)]: ev.target.value } })} className={inp} data-testid={`schedule-date-${e.episode}`} />
              : <span className="text-slate-500">{u ? `Unlocks ${u}` : "No date"}</span>}
            <span className={live ? "text-emerald-300" : "text-amber-300"}>{live ? "Live" : "Scheduled"}</span>
          </div>
        ); })}
        {!d.episodes.length && <p className="text-xs text-slate-500">Add panels with episode numbers first.</p>}
      </div>
      <p className="text-[11px] text-slate-500">Episodes unlock at midnight UTC on their day. Locked episodes are hidden from your showcase, and readers see when the next one arrives.</p>
      <Button size="sm" onClick={save} className="bg-amber-600" data-testid="schedule-save">Save schedule</Button>
    </div>
  );
}
